import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
// import isEqual from "react-fast-compare";
import { css } from "@emotion/react";
import { unwrapResult } from "@reduxjs/toolkit";
import { camelizeKeys } from "humps";
import { stringify } from "qs";
import { Button } from "main/javascripts/components/button/Button";
import { HotelDatePickerBlock } from "main/javascripts/components/hotel/HotelDatePickerBlock";
import { GuestsBlock } from "main/javascripts/components/hotel/GuestsBlock";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { updateHotelShowUrl } from "main/javascripts/utils/HotelUtil";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import {
  hotelShowParamSelector,
  hotelShowParamToFormValues,
  initHotelShowParam,
} from "main/javascripts/slices/paramSlice";
import {
  convertHotelSearchFormValuesToParams,
  fetchHotelRooms,
  hotelIdSelector,
  setIsLoadingRoom,
  setRequestRoomError,
} from "../../hotelSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { backgroundColor } from "main/javascripts/styles/base/colorStyle";
import { yupDefaultMessages } from "main/javascripts/constants/FormConstants";
import { loadingSelector } from "../../searchHotelSlice";
import { useRouter } from "next/router";

type HotelRoomSearchFormData = {
  form?: any; // エラー用
  checkin_date: string;
  checkout_date: string;
  guests: any[];
  number_of_rooms: number;
};

yup.setLocale(yupDefaultMessages);
const schema = yup.object().shape({
  checkin_date: yup.string().nullable().required(),
  checkout_date: yup.string().nullable().required(),
  guests: yup.array().of(
    yup.object().shape({
      number_of_children: yup
        .number()
        .nullable()
        .transform((_, val) => (val || val === 0 ? Number(val) : null))
        .required(),
      ages_of_children: yup.array().when("number_of_children", {
        is: (val) => Number(val) > 0,
        then: yup.array().of(
          yup.object().shape({
            age: yup
              .number()
              .nullable()
              .transform((_, val) => (val || val === 0 ? Number(val) : null))
              .required(),
          })
        ),
      }),
    })
  ),
});

export const HotelRoomSearchForm: React.FC = (): React.ReactElement => {
  const appDispatch = useAppDispatch();
  const router = useRouter();

  // public shouldComponentUpdate(nextProps: any, nextState: any): boolean {
  //   return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  // }

  const hotelId = useAppSelector(hotelIdSelector);
  const param = useAppSelector(hotelShowParamSelector);
  const isLoading = useAppSelector(loadingSelector);

  const methods = useForm<HotelRoomSearchFormData>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: hotelShowParamToFormValues(param),
  });
  const { handleSubmit, clearErrors } = methods;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    clearErrors("form");
    handleSubmit(onSubmit)(e);
  };

  const onSubmit: (values: any) => void = async (values: any) => {
    appDispatch(setIsLoadingRoom(true));
    appDispatch(setRequestRoomError(null));
    const submitValues = convertHotelSearchFormValuesToParams(values);
    appDispatch(
      fetchHotelRooms({
        hotelId: hotelId,
        param: stringify(submitValues, { arrayFormat: "indices" }),
      })
    )
      .then(unwrapResult)
      .then((result) => {
        const newParam: any = {
          ...submitValues,
          cache_id: param.cacheId,
          room_cache_id: result.roomCacheId,
        };
        appDispatch(initHotelShowParam(camelizeKeys(newParam)));
        updateHotelShowUrl(newParam, router);
        appDispatch(setIsLoadingRoom(false));
      })
      .catch((error: any) => {
        appDispatch(setRequestRoomError(error));
        appDispatch(setIsLoadingRoom(false));
      });
  };

  return (
    <div css={blockStyle}>
      <div css={innerStyle}>
        <FormProvider {...methods}>
          <form onSubmit={handleFormSubmit}>
            <HotelDatePickerBlock labelColorStyleKey="default" />
            <GuestsBlock isGuestMultipleColumn={false} />
            <div css={submitBlockStyle}>
              <div css={buttonStyle}>
                <Button type="submit" disabled={isLoading}>
                  更新する
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

const submitBlockStyle = css`
  padding: ${space.atom} 0 ${space.atom2x};
  ${moreThanBreakpoint("tablet")} {
    width: 50%;
    margin: 0 auto;
  }
`;
const buttonStyle = css`
  padding: ${space.atom};
`;
const blockStyle = css`
  background-color: ${backgroundColor.secondaryLightColor};
  padding: ${space.atom} 0;
  box-sizing: border-box;
`;
const innerStyle = css`
  max-width: 600px;
  margin: 0 auto;
`;
